import React, { useState } from "react";
import PropTypes from "prop-types";
import { Badge, Button, Card, CardHeader, CellText, DotsVertical, EmptyState, Menu, Plus, Table, TextInput } from "@opendate-app/setlist";
import client from "../../shared/api/client";
import { toastError, toastSuccess } from "../../shared/toastHelper";
import PartnerDialog from "./PartnerDialog";

const RULE_PREFIX = { domain: "", promo_code: "Code ", trackable_link: "Link " };

const errorMessages = (error) => error?.response?.data?.errors || [];

const AttributionPartnersApp = ({ partners: initialPartners, settings, trackableLinkOptions, paths }) => {
  const [partners, setPartners] = useState(initialPartners);
  const [windowDays, setWindowDays] = useState(String(settings.defaultWindowDays));
  const [savingWindow, setSavingWindow] = useState(false);
  const [dialog, setDialog] = useState(null);

  const mutate = async (method, url, body, failureMessage) => {
    try {
      const { data } = await client[method](url, body);
      setPartners(data.partners);
      return { ok: true, errors: [] };
    } catch (error) {
      const errors = errorMessages(error);
      toastError(errors.length ? errors.join(" ") : failureMessage);
      return { ok: false, errors };
    }
  };

  const saveWindow = async () => {
    setSavingWindow(true);
    try {
      const { data } = await client.patch(paths.settings, { settings: { default_window_days: windowDays } });
      setWindowDays(String(data.settings.defaultWindowDays));
      toastSuccess("Attribution window saved.");
    } catch (error) {
      const errors = errorMessages(error);
      toastError(errors.length ? errors.join(" ") : "Could not save the attribution window.");
    } finally {
      setSavingWindow(false);
    }
  };

  const open = (mode, partner = null) => setDialog({ mode, partner });
  const partnerPath = (partner) => `${paths.partners}/${partner.id}`;

  const columns = [
    { key: "name", header: "Partner", primary: true, render: (partner) => <CellText primary={partner.name} /> },
    {
      key: "kind",
      header: "Type",
      render: (partner) => <Badge color={partner.kind === "artist" ? "brand" : "gray"}>{partner.kind === "artist" ? "Artist" : "Community"}</Badge>,
    },
    {
      key: "rules",
      header: "Rules",
      render: (partner) =>
        partner.rules.length === 0 ? (
          <span>None</span>
        ) : (
          <span data-testid={`partner-rules-${partner.id}`}>{partner.rules.map((rule) => `${RULE_PREFIX[rule.ruleType]}${rule.label}`).join(", ")}</span>
        ),
    },
    {
      key: "actions",
      header: "",
      align: "right",
      render: (partner) => (
        <Menu
          placement="bottom-end"
          items={[
            { label: "Rename", onClick: () => open("rename", partner) },
            { label: "Manage rules", onClick: () => open("rules", partner) },
            { label: "Merge into another partner", onClick: () => open("merge", partner) },
            ...(partner.kind === "artist" ? [] : [{ label: "Delete", destructive: true, onClick: () => open("delete", partner) }]),
          ]}
          trigger={<Button variant="tertiary" size="sm" icon={DotsVertical} aria-label={`Actions for ${partner.name}`} />}
        />
      ),
    },
  ];

  return (
    <>
      <Card
        padding="md"
        header={<CardHeader title="Attribution window" supportingText="How long after a visit a purchase still credits that visit." />}
      >
        <div className="d-flex align-items-end" style={{ gap: 12 }}>
          <div style={{ maxWidth: 240 }}>
            <TextInput
              id="attribution-default-window"
              label="Default window"
              type="number"
              min={1}
              trailingText="days"
              value={windowDays}
              onChange={(event) => setWindowDays(event.target.value)}
            />
          </div>
          <Button variant="primary" onClick={saveWindow} disabled={savingWindow}>
            {savingWindow ? "Saving…" : "Save window"}
          </Button>
        </div>
      </Card>

      <Card
        padding="md"
        header={
          <CardHeader
            title="Partners"
            supportingText="Group referring sites, promo codes and tracking links into the partners that drive your sales. Artists are added automatically when their pages send visitors to an event they play."
            actions={
              <Button variant="secondary" icon={Plus} onClick={() => open("create")}>
                Add partner
              </Button>
            }
          />
        }
      >
        <Table
          columns={columns}
          data={partners}
          getRowKey={(partner) => partner.id}
          empty={<EmptyState size="sm" title="No partners yet" description="Add a partner to group the sites, codes and links that send you buyers." />}
        />
      </Card>

      <PartnerDialog
        dialog={dialog}
        partners={partners}
        trackableLinkOptions={trackableLinkOptions}
        paths={paths}
        partnerPath={partnerPath}
        mutate={mutate}
        onClose={() => setDialog(null)}
      />
    </>
  );
};

const partnerShape = PropTypes.shape({
  id: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  kind: PropTypes.string.isRequired,
  rules: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.number.isRequired, ruleType: PropTypes.string.isRequired, label: PropTypes.string })
  ).isRequired,
});

AttributionPartnersApp.propTypes = {
  partners: PropTypes.arrayOf(partnerShape).isRequired,
  settings: PropTypes.shape({ defaultWindowDays: PropTypes.number.isRequired }).isRequired,
  trackableLinkOptions: PropTypes.arrayOf(PropTypes.shape({ value: PropTypes.number.isRequired, label: PropTypes.string.isRequired })).isRequired,
  paths: PropTypes.shape({
    partners: PropTypes.string.isRequired,
    merges: PropTypes.string.isRequired,
    rules: PropTypes.string.isRequired,
    settings: PropTypes.string.isRequired,
  }).isRequired,
};

export default AttributionPartnersApp;
